import React, { useEffect, useState } from 'react';
import { Button, Container, IconWrapper, Input } from './styles';

const MinusIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" role="img" width="16" height="16">
    <path fill="currentColor" stroke="none" d="M26 18H6a2 2 0 010-4h20a2 2 0 010 4z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" role="img" width="16" height="16">
    <path
      fill="currentColor"
      stroke="none"
      d="M18,18H28a2,2,0,0,0,0-4H18V4a2,2,0,0,0-4,0V14H4a2,2,0,0,0-2,2,2,2,0,0,0,2,2H14V28a2,2,0,0,0,2,2,2,2,0,0,0,2-2Z"
    />
  </svg>
);

interface PlusMinusInputProps {
  onChange: (value: number) => void;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  disableKeyboardInput?: boolean;
}

export default function PlusMinusInput({
  onChange,
  defaultValue = 0,
  min = 0,
  max = Infinity,
  step = 1,
  disableKeyboardInput = false,
}: PlusMinusInputProps) {
  const validDefault = typeof defaultValue === 'number' && !isNaN(defaultValue) ? defaultValue : min;
  const initialValue = Math.min(Math.max(validDefault, min), max);

  const [internalValue, setInternalValue] = useState(initialValue);
  const [inputText, setInputText] = useState(initialValue.toString());

  useEffect(() => {
    const newValidDefault = typeof defaultValue === 'number' && !isNaN(defaultValue) ? defaultValue : min;
    const newInitialValue = Math.min(Math.max(newValidDefault, min), max);

    if (newInitialValue !== internalValue) {
      setInternalValue(newInitialValue);
      setInputText(newInitialValue.toString());
    } else if (newInitialValue.toString() !== inputText) {
      // This condition ensures that if only defaultValue changes but results in the same
      // internalValue (e.g. due to min/max constraints), inputText is still updated
      // if defaultValue.toString() is different.
      setInputText(newInitialValue.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, min, max]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setInputText(rawValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(internalValue + step, max);
    if (newValue !== internalValue) {
      setInternalValue(newValue);
      setInputText(newValue.toString());
      onChange(newValue);
    }
  };

  const handleDecrement = () => {
    const newValue = Math.max(internalValue - step, min);
    if (newValue !== internalValue) {
      setInternalValue(newValue);
      setInputText(newValue.toString());
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    let numericValue = parseInt(inputText, 10);

    if (isNaN(numericValue)) {
      numericValue = min;
    }

    const boundedValue = Math.min(Math.max(numericValue, min), max);

    if (boundedValue !== internalValue || inputText !== boundedValue.toString()) {
      setInternalValue(boundedValue);
      setInputText(boundedValue.toString());
      if (boundedValue !== internalValue) {
        onChange(boundedValue);
      }
    }
  };

  return (
    <Container>
      <Button onClick={handleDecrement} disabled={internalValue <= min} aria-label="Decrease value" type="button">
        <IconWrapper>
          <MinusIcon />
        </IconWrapper>
      </Button>
      <Input
        type="number"
        value={inputText}
        onChange={handleInputChange}
        onBlur={handleBlur}
        min={min}
        max={max}
        step={step}
        aria-label="Quantity input"
        readOnly={disableKeyboardInput}
        tabIndex={disableKeyboardInput ? -1 : undefined}
      />
      <Button onClick={handleIncrement} disabled={internalValue >= max} aria-label="Increase value" type="button">
        <IconWrapper>
          <PlusIcon />
        </IconWrapper>
      </Button>
    </Container>
  );
}
